import { apiClient } from '../lib/apiClient';

/** CTV-474: which side bound the flight. */
export type ConstraintKind = 'geo' | 'station' | 'both';

export interface FlightFeasibilityResult {
  installmentNumber: number | null;
  startDate: string;
  endDate: string;
  daysInFlight: number;
  naturalDaysInFlight: number;
  isPartial: boolean;
  flightBudget: number;
  requestedImpressions: number;
  availableImpressions: number;
  bookedImpressions: number;
  sovCappedImpressions: number;
  netAvailableImpressions: number;
  utilization: number;
  feasible: boolean;
  /** CTV-474: replaces geoLockTripped. */
  constraintKind: ConstraintKind;
  dailyBidRequestsAverage: number;
  /** CTV-474 diagnostic-only — see DTO doc. */
  stationAvailableImpressions: number;
  stationBookedImpressions: number;
  stationSovCappedImpressions: number;
  stationNetAvailableImpressions: number;
  stationDailyBidRequestsAverage: number;
  geoAvailableImpressions: number | null;
  geoBookedImpressions: number | null;
  geoSovCappedImpressions: number | null;
  geoNetAvailableImpressions: number | null;
  geoDailyBidRequestsAverage: number | null;
}

export interface BookedLineItemContribution {
  lineItemId: number;
  lineItemName: string;
  quantity: number;
  delivered: number;
  remaining: number;
  daysLeft: number;
  perDayBooked: number;
  overlapDays: number;
  contribution: number;
  /**
   * CTV-536: 0–1 share of the LI's total fanned-out booked load landing on
   * this station. Non-null on the station-wide (no buyer geo) path; null on
   * the per-geo path.
   */
  channelShare: number | null;
  sites: number[] | null;
  channelGroupIds: number[] | null;
}

export interface FlightDiagnostic {
  flightIndex: number;
  rangeStart: string;
  rangeEnd: string;
  bookedTotal: number;
  daysInWindow: number;
  lineItems: BookedLineItemContribution[];
}

/** CTV-570: per-phase wall-clock breakdown mirrored from the API DTO. */
export interface FeasibilityDiagnosticTimings {
  totalMs: number;
  computeFlightBreakdownMs: number;
  searchExtensionDateMs: number | null;
  computeGeoCoverageMs: number | null;
  computeGeoRecommendationsMs: number | null;
  parallelBandWallClockMs: number | null;
  extensionProbes?: {
    coarseCount: number;
    refineCount: number;
    eomCount: number;
    probes: {
      phase: 'coarse' | 'refine' | 'eom';
      date: string;
      ms: number;
    }[];
  };
}

export interface FeasibilityDiagnostic {
  bookedFormula: string;
  perFlight: FlightDiagnostic[];
  /** CTV-474: iterative extension-date search candidates. */
  extensionSearch?: {
    candidates: { date: string; feasible: boolean }[];
    cappedAt: string | null;
  };
  /** CTV-570: per-phase wall-clock timings (dev-only). */
  timings?: FeasibilityDiagnosticTimings;
}

/** CTV-474: geography kind currently active in custom targeting mode. */
export type GeoKind = 'radius' | 'zips' | 'dmas' | 'states';

/** CTV-474: share of station bid-request volume covered by the selection. */
export interface GeoCoverage {
  kind: GeoKind;
  /** 0–100, rounded. */
  percent: number;
}

/** CTV-474: top-N unselected geos to recommend the buyer add. */
export interface GeoRecommendations {
  kind: GeoKind;
  codes: string[];
  labels: string[];
}

/** CTV-474: station's configured home-market center. */
export interface StationCenter {
  lat: number;
  lon: number;
  label: string;
}

export interface CampaignFeasibilityResponse {
  feasible: boolean;
  failingFlightIndexes: number[];
  requestedImpressionsTotal: number;
  netAvailableImpressionsTotal: number;
  renderRate: number;
  isDefaultRenderRate: boolean;
  flightBreakdown: FlightFeasibilityResult[];
  productName: string;
  stationName: string;
  cpm: number;
  unmatchedLineItemCount: number;
  /** CTV-474: earliest end-date at which every flight is feasible, or null. */
  extensionDate: string | null;
  /** CTV-474: % of station audience covered by the buyer's selection. */
  geoCoverage: GeoCoverage | null;
  /** CTV-474: top-N unselected geos to recommend (only when coverage < 75%). */
  geoRecommendations: GeoRecommendations | null;
  /** CTV-474: station home-market coordinates for the radius recenter button. */
  stationCenter: StationCenter | null;
  diagnostic?: FeasibilityDiagnostic;
}

export interface CampaignFeasibilityInput {
  productId: string;
  startDate: string;
  endDate: string;
  budget: number;
  paymentPlan: 'upfront' | 'monthly';
  targetingMode: 'default' | 'custom';
  geoTargetZips?: string[];
  /** CTV-474. */
  geoTargetDMAs?: string[];
  /** CTV-474. */
  geoTargetStates?: string[];
  excludeSubmissionId?: string;
  includeDiagnostic?: boolean;
  // Extend-flow scoping. When set, `budget` is treated as additional-only and
  // only periods whose start is >= extendFromDate are evaluated.
  extendFromDate?: string;
}

export async function checkCampaignFeasibility(
  input: CampaignFeasibilityInput,
  signal?: AbortSignal,
): Promise<CampaignFeasibilityResponse> {
  return apiClient.post<CampaignFeasibilityResponse>(
    '/inventory/campaign-feasibility',
    input,
    { anonymous: true, signal },
  );
}
